// פורמט he-IL לתאים (11 §1; מ-v1 answer/web/app.js `fmtCell`). טהור — בלי DOM, נבדק ב-vitest.
import type { Col, ColType } from '@aibify/core';

const TZ = 'Asia/Jerusalem';
const nf = new Intl.NumberFormat('he-IL');
const nf2 = new Intl.NumberFormat('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const cf = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', maximumFractionDigits: 2 });
const pf = new Intl.NumberFormat('he-IL', { style: 'percent', maximumFractionDigits: 1 });
const df = new Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: TZ });
const dtf = new Intl.DateTimeFormat('he-IL', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: TZ,
});

export const EMPTY = '–';

/** web-visual-identity: בלי מקף ארוך בטקסט שהלקוח רואה (כותרות בלוקים וטקסט של אייבי) — "X — Y" → "X, Y".
 *  רשת ביטחון לפלטפורמה; ההנחיה לאייבי (common.md) אומרת את אותו דבר. נתוני תאים לא עוברים כאן. */
export const plainDash = (s: string): string => s.replace(/\s*\u2014\s*/g, ', ');

/** מספר מה-wire: number, או מחרוזת מספרית (decimal מהדרייבר — 06). אחרת null. */
export function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '' && /^-?\d+(\.\d+)?$/.test(v.trim())) return Number(v);
  return null;
}

/** תאריך בלבד (YYYY-MM-DD) מתפרש כיום קלנדרי — בלי הזזת אזור זמן. */
function toDate(v: unknown): Date | null {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v !== 'string' && typeof v !== 'number') return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return new Date(`${v}T12:00:00Z`);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtCell(v: unknown, type: ColType | undefined): string {
  if (v === null || v === undefined || v === '') return EMPTY;
  switch (type) {
    case 'number': {
      const n = toNumber(v);
      return n === null ? String(v) : Number.isInteger(n) ? nf.format(n) : nf2.format(n);
    }
    case 'currency': {
      const n = toNumber(v);
      return n === null ? String(v) : cf.format(n);
    }
    case 'percent': {
      // אחוז על ה-wire הוא שבר (0.125 = 12.5%), כמו ב-v1.
      const n = toNumber(v);
      return n === null ? String(v) : pf.format(n);
    }
    case 'date': {
      const d = toDate(v);
      return d ? df.format(d) : String(v);
    }
    case 'datetime': {
      const d = toDate(v);
      return d ? dtf.format(d) : String(v);
    }
    default:
      return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }
}

export const isNumericType = (t: ColType | undefined): boolean => t === 'number' || t === 'currency' || t === 'percent';

export function isNegative(v: unknown): boolean {
  const n = toNumber(v);
  return n !== null && n < 0;
}

/** "נכון ל-" — null → "ללא נתוני זמן" (11 §4). */
export function fmtAsOf(asOf: string | null | undefined): string {
  if (!asOf) return 'ללא נתוני זמן';
  const d = toDate(asOf);
  return d ? `נכון ל-${dtf.format(d)}` : 'ללא נתוני זמן';
}

/** "לפני 5 דקות" — אותו ניסוח כמו בדוח (report@2, fmtAgoWords של web); app@2: שורת המטמון של בלוק app. */
export function fmtAgo(iso: string, now = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const min = Math.max(0, Math.floor((now - t) / 60_000));
  if (min < 1) return 'לפני רגע';
  if (min < 60) return min === 1 ? 'לפני דקה' : `לפני ${min} דקות`;
  const h = Math.floor(min / 60);
  if (h < 24) return h === 1 ? 'לפני שעה' : h === 2 ? 'לפני שעתיים' : `לפני ${h} שעות`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'לפני יום' : d === 2 ? 'לפני יומיים' : `לפני ${d} ימים`;
}

/**
 * app@2: שורת המארח על הקריאות שנעשו בטעינה — "נכון ל-" הישן ביותר, "חושב לפני" של החישוב הישן ביותר, "מחשב מחדש…" אם אחת
 * מהן בחישוב. אין meta (פעולות בלי cache / app@1) → null.
 */
export function loadFreshness(metas: Array<{ asOf: string | null; computedAt: string; recomputing: boolean }>): { asOf: string | null; computedAt: string; recomputing: boolean } | null {
  if (!metas.length) return null;
  const asOfs = metas.map((m) => m.asOf).filter((x): x is string => !!x).sort();
  const computed = metas.map((m) => m.computedAt).sort();
  return { asOf: asOfs[0] ?? null, computedAt: computed[0]!, recomputing: metas.some((m) => m.recomputing) };
}

/** קיצור לציר: 1.2M / 340K. */
export function fmtAxis(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(1).replace(/\.0$/, '')}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  // 1,500 → "1.5K" (לא "2K"); מעל 10K — שלם
  if (a >= 1e3) return `${(n / 1e3).toFixed(a >= 1e4 ? 0 : 1).replace(/\.0$/, '')}K`;
  return nf.format(n);
}

/**
 * "קוד · תיאור" (11 §1): עמודת `id` לעולם לא מוצגת לבד.
 * עמודת התיאור = עמודה שאינה `id` עם אותו `entity`; ואם אין `entity` משותף — עמודה בשם
 * `<key>Name` / `<key>_name` / `<key>Desc` / `<key>_desc`. עמודת התיאור נבלעת בתא הקוד.
 */
export type ViewCol = Col & { descKey?: string };

const DESC_SUFFIXES = ['Name', '_name', 'Desc', '_desc', 'NameHe', '_name_he'];

export function planColumns(columns: Col[]): ViewCol[] {
  const used = new Set<string>();
  const out: ViewCol[] = columns.map((c) => ({ ...c }));
  for (const c of out) {
    if (c.type !== 'id') continue;
    const desc =
      (c.entity && columns.find((d) => d.type !== 'id' && d.entity === c.entity && !used.has(d.key))) ||
      columns.find((d) => d.type === 'text' && DESC_SUFFIXES.some((s) => d.key === c.key + s) && !used.has(d.key));
    if (desc) {
      c.descKey = desc.key;
      used.add(desc.key);
    }
  }
  return out.filter((c) => !used.has(c.key));
}

/** תוכן תא id: "קוד · תיאור", או הקוד + דגל "ללא תיאור". */
export function idCell(row: Record<string, unknown>, c: ViewCol): { code: string; desc: string | null } {
  const code = fmtCell(row[c.key], 'text');
  const raw = c.descKey ? row[c.descKey] : undefined;
  const desc = raw === null || raw === undefined || raw === '' ? null : String(raw);
  return { code, desc };
}
